import axios from 'axios';
import urlJoin from 'url-join';

import {
    ADDRESS_GEOTYPE_LABEL,
    BOROUGH_GEOTYPE_LABEL,
    GEOTYPE_ADDRESSES,
    GEOTYPE_ARRMUNS,
    GEOTYPE_CITIES,
    GEOTYPE_SUBCITIES,
    NEIGHBORHOOD_GEOTYPE_LABEL,
    STREET_GEOTYPE_LABEL,
} from 'constants/Global';

/**
 * Estima Form API Service.
 */
export default class GeoService {
    /**
     * API global search
     * @param {String} query An address, city....
     * @param {Object} params Additionnal options (`types`, `engine`...)
     * @return {Promise}
     */
    static search(query, params = {}) {
        return axios.get(MA.Config.GEOAPI_URL, {
            params: {
                q: query,
                ...params,
            },
            withCredentials: false,
        });
    }

    /**
     * Fetch
     * @return {Promise} Request
     */
    static getCitiesFromZip(zip) {
        const axiosParams = {
            params: {
                q: zip,
                types: `${GEOTYPE_CITIES},${GEOTYPE_ARRMUNS},${GEOTYPE_SUBCITIES}`,
            },
            withCredentials: false,
        };
        return axios.get(MA.Config.GEOAPI_URL, axiosParams);
    }

    /**
     * Find matching addresses for given address components
     * @param {String} address
     * @param {String} cityName
     * @param {String} zip
     * @return {Promise} Request
     */
    static getAddresses(address, cityName, zip) {
        const query = `${address} ${zip} ${cityName}`;
        const axiosParams = {
            params: {
                q: query,
                types: GEOTYPE_ADDRESSES,
                engines: 'algolia',
            },
        };
        return axios.get(MA.Config.GEOAPI_URL, axiosParams);
    }

    /**
     * Fetch an id to find a record of geotype city, armmun or subcity
     * @param {Number} cityId
     * @return {Promise} Request
     */
    static getCityOrArmunOrSubcity(cityId) {
        return GeoService.getCity(cityId).catch((cityError) => {
            if (cityError.response && cityError.response.status === 404) {
                return GeoService.getArrmun(cityId).catch((arrmunError) => {
                    if (arrmunError.response && arrmunError.response.status === 404) {
                        return GeoService.getSubcity(cityId);
                    }
                    return arrmunError;
                });
            }
            return cityError;
        });
    }

    /**
     * Fetch a city
     * @param {Number} cityId
     * @return {Promise} Request
     */
    static getCity(cityId) {
        const fields = 'id,name_short,lat,lng,name,slug,geotype_label';
        const axiosParams = {
            params: {
                fields,
            },
        };
        return axios.get(
            urlJoin(MA.Config.GEOAPI_URL, GEOTYPE_CITIES, String(cityId)),
            axiosParams,
        );
    }

    /**
     * Fetch an arrmun
     * @param {Number} arrmunId
     * @return {Promise} Request
     */
    static getArrmun(arrmunId) {
        const fields = 'id,name_short,lat,lng,name,slug,geotype_label,city';
        const axiosParams = {
            params: {
                fields,
            },
        };
        return axios.get(
            urlJoin(MA.Config.GEOAPI_URL, GEOTYPE_ARRMUNS, String(arrmunId)),
            axiosParams,
        );
    }

    /**
     * Fetch a subcity
     * @param {Number} subcityId
     * @return {Promise} Request
     */
    static getSubcity(subcityId) {
        const fields = 'id,name_short,lat,lng,name,slug,geotype_label,city';
        const axiosParams = {
            params: {
                fields,
            },
        };
        return axios.get(
            urlJoin(MA.Config.GEOAPI_URL, GEOTYPE_SUBCITIES, String(subcityId)),
            axiosParams,
        );
    }

    /**
     *
     * @param {{id: Number, slug: String, geotype_label: String}} place
     * @return {Promise<String>}
     */
    static getSearchFormUrl(place) {
        return new Promise((resolve) => {
            if (place.geotype_label === ADDRESS_GEOTYPE_LABEL) {
                axios
                    .get(urlJoin(MA.Config.GEOAPI_URL, 'addresses', String(place.id)), {
                        fields: 'street,city',
                    })
                    .then((addressResponse) => {
                        const address = addressResponse.data.response.place;
                        axios
                            .get(
                                urlJoin(MA.Config.GEOAPI_URL, 'streets', String(address.street.id)),
                                {
                                    params: { fields: 'id,slug' },
                                },
                            )
                            .then((streetResponse) => {
                                const street = streetResponse.data.response.place;
                                const url = urlJoin(
                                    address.city.slug,
                                    `${street.slug}-${street.id}`,
                                    place.slug,
                                );
                                resolve(url);
                            });
                    });
            } else if (place.geotype_label === STREET_GEOTYPE_LABEL) {
                axios
                    .get(urlJoin(MA.Config.GEOAPI_URL, 'streets', String(place.id)), {
                        params: { fields: 'id,slug,city' },
                    })
                    .then((streetResponse) => {
                        const street = streetResponse.data.response.place;
                        const url = urlJoin(street.city.slug, `${street.slug}-${street.id}`);
                        resolve(url);
                    });
            } else if (
                place.geotype_label === NEIGHBORHOOD_GEOTYPE_LABEL ||
                place.geotype_label === BOROUGH_GEOTYPE_LABEL
            ) {
                axios
                    .get(
                        urlJoin(
                            MA.Config.GEOAPI_URL,
                            `${place.geotype_label.toLowerCase()}s`,
                            String(place.id),
                        ),
                        { params: { fields: 'id,slug,city' } },
                    )
                    .then((response) => {
                        const borough = response.data.response.place;
                        const url = urlJoin(borough.city.slug, `${borough.slug}-${borough.id}`);
                        resolve(url);
                    });
            } else {
                resolve(place.slug);
            }
        });
    }
}
